import type { DataTableColumn } from './types';

/** Joins class names, dropping falsy entries. */
export function cx(...parts: Array<string | false | null | undefined>): string {
    return parts.filter(Boolean).join(' ');
}

/**
 * The columns actually rendered. `visible` defaults to true, so a conditional
 * column is expressed as `visible: showPractice` rather than by splicing the
 * array.
 *
 * Everything positional derives from this one list - the `<colgroup>`, the
 * header cells, the body cells and the empty/loading `colSpan`. A
 * hand-maintained column-count constant is exactly the kind of thing that
 * misaligns a whole table and fails silently.
 */
export function visibleColumns<T, K extends string>(
    columns: ReadonlyArray<DataTableColumn<T, K>>
): ReadonlyArray<DataTableColumn<T, K>> {
    return columns.filter((column) => column.visible !== false);
}

/**
 * The row rule. Painted on the cells, not the `<tr>`: both tables render with
 * `border-collapse: separate` (see `TABLE_STYLE` in `DataTable.tsx`), and the
 * separated border model ignores borders on table rows.
 */
export const ROW_BORDER = 'border-b border-gray-200';

/** Shared geometry for a `<td>`: universal chrome only, padding stays per-table. */
export function cellClasses<T, K extends string>(column: DataTableColumn<T, K>): string {
    return cx(
        'align-middle',
        column.truncate && 'truncate',
        column.align === 'right' && 'text-right',
        column.align === 'center' && 'text-center',
        column.cellClassName
    );
}

/**
 * Header cell chrome. Mirrors the apps' `.text-header` component class, but
 * inlined as utilities so this package doesn't depend on app-level CSS - and
 * deliberately without `.text-header`'s `px-6`, so per-table padding no longer
 * needs `!important` to beat it.
 */
export const HEADER_CELL_BASE =
    'bg-white py-0 text-left align-middle font-inter font-bold text-[12px] leading-4 tracking-[0.005em] text-neutral-500';
